import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Lock, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Empty, toISO, fmtData } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";

export default function MiaPostazione() {
  const oggi = toISO(new Date());
  const [postazioni, setPostazioni] = useState(null);
  const [attiva, setAttiva] = useState(null);
  const [squadraSel, setSquadraSel] = useState(null);
  const [punteggio, setPunteggio] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/postazioni/mie?data=${oggi}`).then((r) => {
      setPostazioni(r.data);
      if (r.data.length === 1) setAttiva(r.data[0]);
    });
  }, [oggi]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const salva = async () => {
    if (punteggio === "") return;
    setSaving(true);
    try {
      await api.post(`/postazioni/${attiva.id}/risultati?data=${oggi}`, { squadra_id: squadraSel.id, punteggio: Number(punteggio) });
      toast.success(`Punteggio di ${squadraSel.nome} salvato!`);
      setSquadraSel(null);
      setPunteggio("");
      const { data } = await api.get(`/postazioni/mie?data=${oggi}`);
      setPostazioni(data);
      setAttiva(data.find((p) => p.id === attiva.id) || null);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (!postazioni) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  return (
    <div data-testid="mia-postazione-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">La mia Postazione</h1>
      <p className="text-sm text-slate-500 font-semibold mb-6">{fmtData(oggi)}</p>

      {postazioni.length === 0 ? (
        <Empty icon={Target} text="Nessuna postazione assegnata oggi" sub="Quando i Responsabili ti assegneranno a una postazione la troverai qui" />
      ) : !attiva ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {postazioni.map((p) => (
            <button key={p.id} className="card-app p-6 text-left hover:-translate-y-1 hover:shadow-lg transition-all" onClick={() => setAttiva(p)} data-testid={`mia-postazione-${p.id}`}>
              <Target className="h-8 w-8 text-amber-500 mb-3" />
              <p className="font-display font-extrabold text-slate-900 text-lg">{p.nome}</p>
              <p className="text-xs text-slate-400 font-semibold">{p.gioco_nome || "Gioco non assegnato"} · {p.squadre.length} squadre</p>
            </button>
          ))}
        </div>
      ) : squadraSel ? (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-app p-6 max-w-md mx-auto" data-testid="inserimento-punteggio">
          <button className="text-xs font-bold text-slate-400 mb-4" onClick={() => { setSquadraSel(null); setPunteggio(""); }} data-testid="punteggio-indietro-btn">← Cambia squadra</button>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-5 w-5 rounded-full" style={{ background: squadraSel.colore || "#94a3b8" }} />
            <h2 className="font-display text-xl font-extrabold text-slate-900">Quanti punti hanno fatto i {squadraSel.nome}?</h2>
          </div>
          <input
            type="number"
            inputMode="numeric"
            autoFocus
            className="input-app !text-4xl !py-5 text-center font-display font-extrabold mb-4"
            placeholder="0"
            value={punteggio}
            onChange={(e) => setPunteggio(e.target.value)}
            data-testid="punteggio-grezzo-input"
          />
          <p className="text-xs text-slate-400 font-semibold text-center mb-4">⚠️ Una volta salvato non potrai più modificarlo: solo un Responsabile può correggerlo.</p>
          <button className="btn-primary w-full !py-4 !text-base" disabled={saving || punteggio === ""} onClick={salva} data-testid="punteggio-conferma-btn">
            {saving ? "Salvataggio..." : "Salva punteggio"}
          </button>
        </motion.div>
      ) : (
        <div>
          {postazioni.length > 1 && (
            <button className="text-xs font-bold text-slate-400 mb-3" onClick={() => setAttiva(null)} data-testid="cambia-postazione-btn">← Tutte le mie postazioni</button>
          )}
          <div className="card-app p-5 mb-4">
            <p className="font-display font-extrabold text-slate-900 text-lg">{attiva.nome}</p>
            <p className="text-xs text-slate-400 font-semibold">{attiva.gioco_nome || "Gioco non assegnato"}</p>
          </div>
          <p className="label-app">Scegli la squadra</p>
          <div className="grid grid-cols-2 gap-3">
            {attiva.squadre.map((s) => {
              const ris = attiva.risultati.find((r) => r.squadra_id === s.id);
              return ris ? (
                <div key={s.id} className="card-app p-5 opacity-70" data-testid={`squadra-bloccata-${s.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-4 w-4 rounded-full" style={{ background: s.colore || "#94a3b8" }} />
                    <p className="font-display font-extrabold text-slate-700 truncate">{s.nome}</p>
                  </div>
                  <p className="badge-pos inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {ris.punteggio_grezzo} punti</p>
                  <p className="text-[10px] text-slate-400 font-bold mt-2 flex items-center gap-1"><Lock className="h-3 w-3" /> Salvato</p>
                </div>
              ) : (
                <button key={s.id} className="card-app p-5 text-left hover:-translate-y-1 hover:shadow-lg transition-all border-2 border-transparent hover:border-amber-300"
                  onClick={() => setSquadraSel(s)} data-testid={`squadra-scegli-${s.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="h-4 w-4 rounded-full" style={{ background: s.colore || "#94a3b8" }} />
                    <p className="font-display font-extrabold text-slate-900 truncate">{s.nome}</p>
                  </div>
                  <p className="text-xs font-bold text-amber-600">Inserisci punteggio →</p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
